<template>
    <div class="single-vehicle-editor">
        <div class="vehicle-header">
            <h4>{{ $t("route_optimization.default_values") }}</h4>
        </div>

        <div class="vehicle-presets">
            <span
                v-for="preset in activePresets"
                :key="preset.name"
                class="routa-pill"
                :class="{ active: isPresetActive(preset) }"
                @click="applyPreset(preset)"
            >
                {{ $t(preset.name) }}
            </span>
        </div>

        <div class="vehicle-card">
            <div class="row g-2 mb-2">
                <div
                    v-if="operationType !== 'road_inspection'"
                    class="col-12 col-lg-3"
                >
                    <label class="field-label">{{ $t("route_optimization.work_width") }}</label>
                    <input
                        v-model.number="vehicle.properties.work_width"
                        type="number"
                        class="form-control form-control-sm"
                        :class="{ 'is-invalid': isFieldInvalid('work_width') }"
                        step="0.1"
                        min="0"
                    >
                </div>

                <div class="col-12 col-lg-3">
                    <label class="field-label">{{ $t("route_optimization.max_speed") }}</label>
                    <input
                        v-model.number="vehicle.properties.max_speed"
                        type="number"
                        class="form-control form-control-sm"
                        :class="{ 'is-invalid': isFieldInvalid('max_speed') }"
                        step="1"
                        min="0"
                    >
                </div>

                <div
                    v-if="operationType !== 'road_inspection'"
                    class="col-12 col-lg-3"
                >
                    <label class="field-label">{{ $t("route_optimization.work_speed") }}</label>
                    <input
                        v-model.number="vehicle.properties.work_speed"
                        type="number"
                        class="form-control form-control-sm"
                        :class="{ 'is-invalid': isFieldInvalid('work_speed') }"
                        step="1"
                        min="0"
                    >
                </div>
            </div>

            <div
                v-if="operationType !== 'road_inspection'"
                class="row g-2 mb-3 mt-3"
            >
                <div class="col-12">
                    <div class="form-check">
                        <input
                            id="includeReturnTrip"
                            v-model="vehicle.properties.include_return_trip"
                            type="checkbox"
                            class="form-check-input"
                        >
                        <label
                            class="form-check-label"
                            for="includeReturnTrip"
                        >
                            {{ $t("route_optimization.include_return_trip") }}
                        </label>
                    </div>
                </div>
            </div>

            <div class="row g-2">
                <div class="col-12">
                    <RouteOptimizationCoordinateEditor
                        :model-value="{ x: vehicle.geometry.coordinates[0], y: vehicle.geometry.coordinates[1] }"
                        :title="$t('route_optimization.start_location')"
                        :point-type="1"
                        :show-buttons="false"
                        :skip-defaults="true"
                        :allow-auto-position="!resubmitJobId && !vehicle.properties.locationManuallySet"
                        :class="{ 'invalid-coordinates': isFieldInvalid('coordinates') }"
                        @update:model-value="updateLocation"
                        @manual-position-set="setManualPosition"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import RouteOptimizationCoordinateEditor from "@/components/routeoptimization/RouteOptimizationCoordinateEditor.vue"

export default {
    name: "SingleVehicleEditor",
    components: {
        RouteOptimizationCoordinateEditor
    },
    props: {
        modelValue: {
            type: Object,
            default: null
        },
        showValidation: {
            type: Boolean,
            default: false
        },
        selectedGeometry: {
            type: Object,
            default: null
        },
        operationType: {
            type: String,
            default: 'winter_maintenance'
        }
    },
    emits: ['update:modelValue'],
    data() {
        const vehiclePresets = [
            { name: 'route_optimization.truck', work_width: 400, max_speed: 80, work_speed: 25 },
            { name: 'route_optimization.tractor', work_width: 300, max_speed: 40, work_speed: 15 },
            { name: 'route_optimization.skid_steer', work_width: 260, max_speed: 25, work_speed: 8 },
            { name: 'route_optimization.atv', work_width: 200, max_speed: 50, work_speed: 20 }
        ];
        return {
            vehicle: this.modelValue || this.createDefaultVehicle(vehiclePresets[0]),
            selectedPreset: null,
            vehiclePresets
        };
    },
    computed: {
        resubmitJobId() {
            return this.$store.state.routeOptimization.resubmitJobId;
        },
        activePresets() {
            if (this.operationType === 'road_inspection') {
                return [
                    { name: 'route_optimization.car',     max_speed: 160, work_width: 0, work_speed: 0 },
                    { name: 'route_optimization.van',      max_speed: 80,  work_width: 0, work_speed: 0 },
                    { name: 'route_optimization.bicycle',  max_speed: 30,  work_width: 0, work_speed: 0 },
                ]
            }
            return this.vehiclePresets
        },
        isFieldInvalid() {
            return (field) => {
                if (!this.showValidation) return false;
                switch (field) {
                case 'work_width':
                    if (this.operationType === 'road_inspection') return false;
                    return !this.vehicle.properties.work_width || this.vehicle.properties.work_width <= 0 || this.vehicle.properties.work_width >= 1500;
                case 'max_speed':
                    return !this.vehicle.properties.max_speed || this.vehicle.properties.max_speed <= 0 || this.vehicle.properties.max_speed >= 200;
                case 'work_speed':
                    if (this.operationType === 'road_inspection') return false;
                    return !this.vehicle.properties.work_speed || this.vehicle.properties.work_speed <= 0 || this.vehicle.properties.work_speed >= 200;
                case 'coordinates':
                    return !this.vehicle.geometry.coordinates[0] || !this.vehicle.geometry.coordinates[1] || this.vehicle.geometry.coordinates[0] === 0 || this.vehicle.geometry.coordinates[1] === 0;
                default:
                    return false;
                }
            };
        }
    },
    watch: {
        modelValue: {
            handler(newVal) {
                if (newVal && JSON.stringify(newVal) !== JSON.stringify(this.vehicle)) {
                    this.vehicle = { ...newVal };
                }
            },
            deep: true,
            immediate: true
        },
        vehicle: {
            handler(newVal) {
                this.$emit('update:modelValue', newVal);
            },
            deep: true
        },
        selectedGeometry: {
            handler(newGeometry) {
                const routeStart = this.getRouteStartPoint();
                if (routeStart && !this.vehicle.properties.locationManuallySet) {
                    this.vehicle.geometry.coordinates = [...routeStart];
                }
            },
            deep: true
        },
        '$store.state.routeOptimization.maxDurationHours'() {
            this.updateMaxDuration();
        },
        '$store.state.routeOptimization.maxDurationMinutes'() {
            this.updateMaxDuration();
        },
        operationType(newType) {
            // When switching to road inspection, apply the car preset as the default
            if (newType === 'road_inspection') {
                const carPreset = this.activePresets[0]; // { max_speed: 160, work_width: 0, work_speed: 0 }
                this.vehicle.properties.max_speed = carPreset.max_speed;
                this.vehicle.properties.work_width = carPreset.work_width;
                this.vehicle.properties.work_speed = carPreset.work_speed;
                this.vehicle.properties.include_return_trip = false;
                this.selectedPreset = carPreset.name;
            } else {
                // When switching back to winter maintenance, apply the truck preset as the default
                const truckPreset = this.vehiclePresets[0]; // { work_width: 400, max_speed: 80, work_speed: 25 }
                this.vehicle.properties.work_width = truckPreset.work_width;
                this.vehicle.properties.max_speed = truckPreset.max_speed;
                this.vehicle.properties.work_speed = truckPreset.work_speed;
                this.vehicle.properties.include_return_trip = true;
                this.selectedPreset = truckPreset.name;
            }
        }
    },
    methods: {
        createDefaultVehicle(truckPreset) {
            const routeStart = this.getRouteStartPoint();
            const maxDurationSeconds = (this.$store.state.routeOptimization.maxDurationHours || 0) * 3600 + (this.$store.state.routeOptimization.maxDurationMinutes || 0) * 60;
            return {
                type: 'Feature',
                geometry: { type: 'Point', coordinates: routeStart || [25.7482, 62.2426] },
                properties: { work_width: truckPreset.work_width, max_speed: truckPreset.max_speed, work_speed: truckPreset.work_speed, max_duration: maxDurationSeconds, include_return_trip: true, type: 'vehicle' }
            };
        },
        getRouteStartPoint() {
            if (!this.selectedGeometry?.features?.length) return null;

            const firstFeature = this.selectedGeometry.features[0];
            if (firstFeature?.geometry?.coordinates?.length) {
                const coords = firstFeature.geometry.coordinates;
                if (firstFeature.geometry.type === 'LineString') {
                    return [coords[0][0], coords[0][1]];
                } else if (firstFeature.geometry.type === 'MultiLineString' && coords[0]?.length) {
                    return [coords[0][0][0], coords[0][0][1]];
                }
            }
            return null;
        },
        updateLocation(location) {
            this.vehicle.geometry.coordinates = [location.x, location.y];
        },
        setManualPosition() {
            this.vehicle.properties.locationManuallySet = true;
        },
        applyPreset(preset) {
            this.vehicle.properties.work_width = preset.work_width;
            this.vehicle.properties.max_speed = preset.max_speed;
            this.vehicle.properties.work_speed = preset.work_speed;
            this.selectedPreset = preset.name;
        },
        isPresetActive(preset) {
            if (this.operationType === 'road_inspection') {
                return this.vehicle.properties.max_speed === preset.max_speed
            }
            return this.vehicle.properties.work_width === preset.work_width &&
                   this.vehicle.properties.max_speed  === preset.max_speed  &&
                   this.vehicle.properties.work_speed === preset.work_speed
        },
        updateMaxDuration() {
            const maxDurationSeconds = (this.$store.state.routeOptimization.maxDurationHours || 0) * 3600 + (this.$store.state.routeOptimization.maxDurationMinutes || 0) * 60;
            this.vehicle.properties.max_duration = maxDurationSeconds;
        }
    }
};
</script>

<style scoped>
.single-vehicle-editor {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.vehicle-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
}

.vehicle-header h4 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: #333;
}

.vehicle-card {
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 1rem;
}

.field-label {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
    font-weight: 500;
    color: #555;
}

.is-invalid {
    border-color: #dc3545 !important;
}

.invalid-coordinates :deep(input) {
    border-color: #dc3545 !important;
}

.vehicle-presets {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1rem;
    flex-wrap: wrap;
}

.routa-pill {
    background: transparent;
    color: #6c757d;
    border: 2px solid #ced4da;
    padding: 0.4rem 1rem;
    border-radius: 20px;
    cursor: pointer;
    font-size: 0.9rem;
    font-weight: 500;
    transition: all 0.2s;
    user-select: none;
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.routa-pill:hover {
    border-color: #7FC000;
    color: #7FC000;
    background: transparent;
}

.routa-pill.active {
    background: #7FC000;
    color: white;
    border-color: #7FC000;
}
</style>
